import Layout from '../../components/common/Layout'
import dynamic from 'next/dynamic'
import HeaderNew from '../../components/home-new/HeaderNew'
import HeroNew from '../../components/home-new/HeroNew'
const PromotionsNew = dynamic(() => import('../../components/home-new/PromotionsNew'))
const StepsNew = dynamic(() => import('../../components/home-new/StepsNew'))
const FeaturesNew = dynamic(() => import('../../components/home-new/FeaturesNew'))
const BusinessServicesNew = dynamic(() => import('../../components/home-new/BusinessServicesNew'))
const ShippopBusinessNew = dynamic(() => import('../../components/home-new/ShippopBusinessNew'))
const BusinessVideoNew = dynamic(() => import('../../components/home-new/BusinessVideoNew'))
const ClientsNew = dynamic(() => import('../../components/home-new/ClientsNew'))
const CertificationsNew = dynamic(() => import('../../components/home-new/CertificationsNew'))
const NewsNew = dynamic(() => import('../../components/home-new/NewsNew'))
const FooterNew = dynamic(() => import('../../components/home-new/FooterNew'))
import Axios from 'axios'
import getConfig from 'next/config'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

const { publicRuntimeConfig } = getConfig()

const SEO_TITLE = 'SHIPPOP เปรียบเทียบค่าขนส่ง ส่งพัสดุ ขนส่งที่ดีสุด ส่งต่างประเทศ | จองขนส่งออนไลน์'
const SEO_DESCRIPTION = 'SHIPPOP แพลตฟอร์มเปรียบเทียบค่าขนส่งชั้นนำ ส่งพัสดุง่าย เปรียบเทียบราคาขนส่งจาก Flash, DHL, Kerry, J&T, EMS และอีกมากกว่า 10 เจ้า ส่งต่างประเทศกว่า 200 ประเทศ จองขนส่ง พิมพ์ใบปะหน้า ติดตามพัสดุ ครบจบในที่เดียว'
const SEO_KEYWORDS = 'เปรียบเทียบค่าขนส่ง, ส่งพัสดุ, ขนส่งที่ดีสุด, ส่งต่างประเทศ, เช็คค่าส่ง, จองขนส่ง, เปรียบเทียบราคาขนส่ง, ส่งของ, ค่าส่งพัสดุ, SHIPPOP, ส่งด่วน, COD เก็บเงินปลายทาง, ขนส่งราคาถูก'

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'SHIPPOP',
  url: 'https://www.shippop.com',
  description: SEO_DESCRIPTION,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'THB',
    description: 'เปรียบเทียบค่าขนส่ง ส่งพัสดุ ฟรีไม่มีค่าสมัคร',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: '20000',
  },
  provider: {
    '@type': 'Organization',
    name: 'SHIPPOP',
    url: 'https://www.shippop.com',
    logo: 'https://www.shippop.com/static/img/sp-logo.svg',
    sameAs: [
      'https://www.facebook.com/shippop',
      'https://line.me/R/ti/p/@shippop',
    ],
  },
}

const breadcrumbData = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'หน้าแรก',
      item: 'https://www.shippop.com',
    },
  ],
}

// Strip HTML tags + decode the few entities WordPress returns in title/excerpt.
const stripHtml = (s = '') =>
  s
    .replace(/<[^>]*>/g, '')
    .replace(/&#8230;/g, '…')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .trim()

export async function getServerSideProps(context) {
  let news = []

  // Primary: fetch directly from SHIPPOP's public WordPress blog API. This is
  // the real source and is reachable in BOTH local dev and production. The
  // internal /api/blog/fetch proxy depends on a legacy backend that is
  // unreachable in local dev (returns 500), so it's only a fallback here.
  try {
    const res = await Axios.get('https://blog.shippop.com/wp-json/wp/v2/posts', {
      params: { per_page: 3, _embed: 1 },
      timeout: 8000,
    })
    news = (res.data || []).map((p) => ({
      title: stripHtml(p?.title?.rendered),
      desc: stripHtml(p?.excerpt?.rendered),
      link: p?.link,
      date: p?.date,
      img:
        p?._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
        p?.jetpack_featured_media_url ||
        null,
    }))
  } catch (err) {
    // Fallback: the internal proxy (works in prod even if the WP API is blocked).
    const requestProtocol = context.req.headers['x-forwarded-proto'] || 'http'
    const requestHost = context.req.headers.host
    const baseUrlCandidates = [
      publicRuntimeConfig.siteDomain,
      requestHost ? `${requestProtocol}://${requestHost}` : null,
    ].filter(Boolean)
    for (const baseUrl of baseUrlCandidates) {
      try {
        const r = await Axios.get(`${baseUrl}/api/blog/fetch`)
        const fetched = r?.data?.data || []
        if (fetched.length) {
          news = fetched
          break
        }
      } catch (e) {
        // try next candidate
      }
    }
  }

  return {
    props: {
      news,
      ...(await serverSideTranslations(context.locale, [
        'common',
        'header',
        'footer',
        'pdpa',
        'home',
        'checkprice',
      ])),
    },
  }
}

export default function HomeNew({ news }) {
  return (
    <Layout
      title={SEO_TITLE}
      description={SEO_DESCRIPTION}
      keywords={SEO_KEYWORDS}
      noHeader
      noFooter
      headEl={
        <>
          {/* Sukhumvit Set webfont — scoped to the new homepage only */}
          <link
            rel="preload"
            href="/static/fonts/sukhumvitset/SukhumvitSet-Text.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href="/static/fonts/sukhumvitset/SukhumvitSet-Medium.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <style
            dangerouslySetInnerHTML={{
              __html: `
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-Text.woff2') format('woff2');font-weight:400;font-style:normal;font-display:swap;}
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-Medium.woff2') format('woff2');font-weight:500;font-style:normal;font-display:swap;}
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-SemiBold.woff2') format('woff2');font-weight:600;font-style:normal;font-display:swap;}
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-Bold.woff2') format('woff2');font-weight:700;font-style:normal;font-display:swap;}
@property --promo-angle{syntax:'<angle>';initial-value:0deg;inherits:false;}
@keyframes promo-spin{to{--promo-angle:360deg;}}
`,
            }}
          />
          <link rel="canonical" key="canonical" href="https://www.shippop.com/" />
          <meta key="og:type" property="og:type" content="website" />
          <meta key="og:title" property="og:title" content={SEO_TITLE} />
          <meta key="og:description" property="og:description" content={SEO_DESCRIPTION} />
          <meta key="og:image" property="og:image" content="https://www.shippop.com/static/img/home-new/banner.png" />
          <meta key="og:url" property="og:url" content="https://www.shippop.com/" />
          <meta key="og:site_name" property="og:site_name" content="SHIPPOP" />
          <meta key="og:locale" property="og:locale" content="th_TH" />
          <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={SEO_TITLE} />
          <meta name="twitter:description" content={SEO_DESCRIPTION} />
          <meta name="twitter:image" content="https://www.shippop.com/static/img/home-new/banner.png" />
          <meta name="robots" content="index, follow" />
          <meta name="author" content="SHIPPOP" />
          {/* Preload the LCP banner as WebP (the `type` gate means non-WebP
              browsers skip it and fall back to the <picture> PNG source). */}
          <link
            rel="preload"
            href="/static/img/home-new/banner.webp"
            as="image"
            type="image/webp"
            fetchpriority="high"
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
          />
        </>
      }
    >
      <HeaderNew />
      <main>
        <HeroNew />
        <PromotionsNew />
        <StepsNew />
        <FeaturesNew />
        <BusinessServicesNew />
        <ShippopBusinessNew />
        <BusinessVideoNew />
        <ClientsNew />
        <CertificationsNew />
        <NewsNew news={news} />
      </main>
      <FooterNew />
    </Layout>
  )
}
